/*
 * Every font the editor offers, in one list (the font sheet, the print PDF files, the loading rules all read it).
 *
 *   source 'google'  the first nine: on screen from the Google Fonts stylesheet in index.html (loaded at start-up)
 *   source 'local'   the script fonts added later: on screen from /fonts/<slug>-<w>.woff2 (@font-face made by
 *                    src/fonts/load.ts), loaded only when used, so the app does not start slower
 * Every font also has /fonts/<slug>-<w>.ttf for the print/document PDF (text drawn as outlines) and its licence
 * file next to it (OFL, or Apache 2.0 for Satisfy) — all free for commercial use (checked in google/fonts, Oct 2026).
 *
 * `thai: false` = the font has no Thai letters. Thai typed in it is drawn with THAI_FALLBACK (Charm) on screen,
 * in pictures and in video; in the print PDF that box becomes a 300 DPI picture (listed before sending).
 * `weights` = the real weights; a font with only 400 has no bold (the bold button is off for it).
 */

export type FontGroup = 'print' | 'thaiScript' | 'fancy' | 'hand' | 'sign';
export interface FontInfo {
  family: string;
  slug: string;
  weights: number[];
  group: FontGroup;
  thai: boolean;
  source: 'google' | 'local';
  license: 'OFL' | 'Apache-2.0';
}

export const FONT_GROUPS: Array<{ id: FontGroup; name: string }> = [
  { id: 'print', name: 'ตัวพิมพ์' },
  { id: 'thaiScript', name: 'ตัวเขียนไทย' },
  { id: 'fancy', name: 'ตัวเขียนหรู (อังกฤษ)' },
  { id: 'hand', name: 'ลายมือ (อังกฤษ)' },
  { id: 'sign', name: 'หนา ทำป้าย (อังกฤษ)' }
];

const g = (family: string, slug: string, weights: number[], group: FontGroup): FontInfo =>
  ({ family, slug, weights, group, thai: true, source: 'google', license: 'OFL' });
const l = (family: string, slug: string, weights: number[], group: FontGroup, thai: boolean, license: FontInfo['license'] = 'OFL'): FontInfo =>
  ({ family, slug, weights, group, thai, source: 'local', license });

export const FONT_CATALOG: FontInfo[] = [
  g('Sarabun', 'sarabun', [400, 700], 'print'),
  g('Prompt', 'prompt', [400, 600, 700], 'print'),
  g('Kanit', 'kanit', [400, 600, 700], 'print'),
  g('Mitr', 'mitr', [400, 500, 700], 'print'),
  g('Noto Sans Thai', 'noto-sans-thai', [400, 700], 'print'),
  g('Chakra Petch', 'chakra-petch', [400, 700], 'print'),
  g('Bai Jamjuree', 'bai-jamjuree', [400, 700], 'print'),
  g('Pridi', 'pridi', [400, 700], 'print'),
  g('Charm', 'charm', [400, 700], 'thaiScript'),
  l('Charmonman', 'charmonman', [400, 700], 'thaiScript', true),
  l('Srisakdi', 'srisakdi', [400, 700], 'thaiScript', true),
  l('Mali', 'mali', [400, 700], 'thaiScript', true),
  l('Itim', 'itim', [400], 'thaiScript', true),
  l('Sriracha', 'sriracha', [400], 'thaiScript', true),
  l('Kodchasan', 'kodchasan', [400, 700], 'thaiScript', true),
  l('Niramit', 'niramit', [400, 700], 'thaiScript', true),
  l('Great Vibes', 'great-vibes', [400], 'fancy', false),
  l('Allura', 'allura', [400], 'fancy', false),
  l('Parisienne', 'parisienne', [400], 'fancy', false),
  l('Alex Brush', 'alex-brush', [400], 'fancy', false),
  l('Tangerine', 'tangerine', [400, 700], 'fancy', false),
  l('Dancing Script', 'dancing-script', [400, 700], 'hand', false),
  l('Pacifico', 'pacifico', [400], 'hand', false),
  l('Satisfy', 'satisfy', [400], 'hand', false, 'Apache-2.0'),
  l('Sacramento', 'sacramento', [400], 'hand', false),
  l('Caveat', 'caveat', [400, 700], 'hand', false),
  l('Indie Flower', 'indie-flower', [400], 'hand', false),
  l('Shadows Into Light', 'shadows-into-light', [400], 'hand', false),
  l('Lobster', 'lobster', [400], 'sign', false),
  l('Kaushan Script', 'kaushan-script', [400], 'sign', false),
  l('Amatic SC', 'amatic-sc', [400, 700], 'sign', false)
];

/** Thai letters typed in a font without them are drawn with this one */
export const THAI_FALLBACK = 'Charm';
/** loaded at start-up (as before): the nine fonts of the first version */
export const CORE_FONTS = FONT_CATALOG.filter(f => f.source === 'google').map(f => f.family);

const byFamily = new Map(FONT_CATALOG.map(f => [f.family, f]));
/** a saved family name (quotes allowed) → its entry, or undefined for a font the app does not have */
export const fontInfo = (family: unknown): FontInfo | undefined =>
  byFamily.get(String(family ?? '').replace(/^["']|["']$/g, ''));
/** has a real bold (700) face */
export const hasBold = (family: unknown): boolean => !!fontInfo(family)?.weights.includes(700);
/** text has Thai letters */
export const hasThai = (text: unknown): boolean => /[฀-๿]/.test(String(text ?? ''));
